import {
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  DEFAULT_LABEL_COLUMN_WIDTH,
  MIN_LABEL_COLUMN_WIDTH,
  DEFAULT_PADDING,
  DROPDOWN_PADDING_ADJUSTMENT,
  GROUP_HEADER_FONT,
  EXTERNAL_ICON_WIDTH,
  GO_TO_FINDER_WIDTH,
  GO_TO_FINDER_HEADER_CLASS,
} from "../constants"
import { calculateOptimalColumnWidth } from "./text-measurement"
import { getHeaderMinWidth } from "./header-layout"
import { get } from "lodash"

// Freshness dot (8px) plus its right margin (6px), reserved left of the label.
const INTRADAY_FRESHNESS_DOT_ALLOWANCE = 14

function calculateOptimalWidth(sortedContributorsArray, currentLevelData, dropdownTableTitle = false, showsFreshnessDot = false, showsGoToFinder = false) {
  if (!sortedContributorsArray || sortedContributorsArray.length === 0) return MIN_COLUMN_WIDTH

  // Prepare items array including contributors and aggregation data
  const items = [...sortedContributorsArray]

  // Also include the aggregation/total row label if it exists
  if (currentLevelData && currentLevelData.label) {
    items.push(currentLevelData)
  }

  const basePadding = dropdownTableTitle ? DROPDOWN_PADDING_ADJUSTMENT : DEFAULT_PADDING + EXTERNAL_ICON_WIDTH
  const padding = basePadding
    + (showsFreshnessDot ? INTRADAY_FRESHNESS_DOT_ALLOWANCE : 0)
    + (showsGoToFinder ? GO_TO_FINDER_WIDTH : 0)

  // Use GROUP_HEADER_FONT because it's the largest of all fonts.
  return calculateOptimalColumnWidth(items, "label", GROUP_HEADER_FONT, {
    minWidth: MIN_COLUMN_WIDTH,
    maxWidth: MAX_COLUMN_WIDTH,
    defaultWidth: DEFAULT_LABEL_COLUMN_WIDTH,
    padding,
  })
}

const getLabelHeaderName = (idLabel) => idLabel || "ID"

export const hasLabelGoToFinder = ({ onGoToRow, sortedContributorsArray }) => Boolean(onGoToRow && sortedContributorsArray)

export const getLabelColumnDimensions = ({
  idLabel,
  dropdownTableTitle,
  sortedContributorsArray,
  data,
  savedDataGridState,
  intradayPricePrefix,
  hasGoToFinder,
}) => {
  const optimalWidth = calculateOptimalWidth(sortedContributorsArray, data, dropdownTableTitle, Boolean(intradayPricePrefix), hasGoToFinder)
  const headerMinWidth = getHeaderMinWidth(getLabelHeaderName(idLabel), hasGoToFinder)

  const savedWidth = get(savedDataGridState, "columns.dimensions.label.width")
  const hasSavedWidth = savedWidth && savedWidth > 0
  const width = hasSavedWidth
    ? savedWidth
    : Math.max(DEFAULT_LABEL_COLUMN_WIDTH, optimalWidth || 0, headerMinWidth)
  const minWidth = Math.max(MIN_LABEL_COLUMN_WIDTH, hasSavedWidth ? Math.min(headerMinWidth, savedWidth) : headerMinWidth)

  return { width, minWidth }
}

export const createLabelColumn = ({
  idLabel,
  dropdownTableTitle,
  sortMetricId,
  isAbsoluteSort,
  sortArrowDirection,
  metricMetadata,
  onSortChange,
  data,
  sortedContributorsArray,
  handleClickContributor,
  includeDrilldownArrows,
  isTopLevel,
  handleSetTableLevel,
  parentLevelOptions,
  currentGroupId,
  LabelCellComponent,
  CustomHeaderComponent,
  onDrilldownRowClick,
  intradayPricePrefix,
  onGoToRow,
  labelDimensions: { width, minWidth },
}) => {
  const hasGoToFinder = hasLabelGoToFinder({ onGoToRow, sortedContributorsArray })
  const headerName = getLabelHeaderName(idLabel)

  return {
    field: "label",
    headerName,
    type: "string",
    width,
    minWidth,
    align: "left",
    headerAlign: "left",
    headerClassName: hasGoToFinder ? GO_TO_FINDER_HEADER_CLASS : undefined,
    sortable: false, // We handle sorting manually
    renderCell: (params) => (
      <LabelCellComponent
        params={params}
        handleClickContributor={handleClickContributor}
        includeDrilldownArrows={includeDrilldownArrows}
        isTopLevel={isTopLevel}
        handleSetTableLevel={handleSetTableLevel}
        dropdownTableTitle={dropdownTableTitle}
        parentLevelOptions={parentLevelOptions}
        currentGroupId={currentGroupId}
        onDrilldownRowClick={onDrilldownRowClick}
        intradayPricePrefix={intradayPricePrefix}
      />
    ),
    renderHeader: (params) => (
      <CustomHeaderComponent
        field="label"
        colDef={params.colDef}
        sortMetricId={sortMetricId}
        isAbsoluteSort={isAbsoluteSort}
        sortArrowDirection={sortArrowDirection}
        metricMetadata={metricMetadata}
        onSortChange={onSortChange}
        data={data}
        goToRows={hasGoToFinder ? sortedContributorsArray : undefined}
        onGoToRow={hasGoToFinder ? onGoToRow : undefined}
      />
    ),
    pinnable: false,
  }
}
